import { useMemo } from "react";
import { UserData } from "../types/org.type";
import {
  Persona,
  RegtankOnboardingStatusEnum,
} from "@funded-here-interface/common/src/constant/enum";
import { sortByExpiryDateDesc } from "../Utils/regtankUtils";

/**
 * Interface for Regtank director validation result
 * Tracks completion and approval status of director Regtank onboarding
 */
export interface RegtankValidationResult {
  allDirectorsApproved: boolean;
  approvedDirectors: string[];
  pendingDirectors: string[];
}

/**
 * Checks if director has completed Regtank onboarding
 * @returns Object with completion and approval status
 */
const getDirectorStatus = (user: UserData): { approved: boolean } => {
  // No Regtank data means not completed
  if (!user.regtankOnboarding || !user.regtankRequest?.length) {
    return { approved: false };
  }

  // Get most recent onboarding attempt
  const sortedRequests = sortByExpiryDateDesc(user.regtankRequest);
  const mostRecent = sortedRequests[0];

  if (!mostRecent) {
    return { approved: false };
  }

  const approved =
    mostRecent.onboardingStatus === RegtankOnboardingStatusEnum.APPROVED;

  return { approved };
};

/**
 * Hook to validate director Regtank onboarding status
 *
 * Checks if all directors have:
 * 1. Completed their Regtank onboarding
 * 2. Received approval for their onboarding
 *
 * @param directorEmails - Array of director email addresses to validate
 * @param allUsersData - Array of user data containing Regtank info
 * @param role - User persona/role
 * @returns RegtankValidationResult with completion and approval status
 */
export const useRegtankDirectorValidation = (
  directorEmails: string[],
  allUsersData: UserData[] | undefined,
  role: Persona
): RegtankValidationResult => {
  return useMemo(() => {
    // For DISTRIBUTOR role or missing data, return default (no validation)
    if (role === Persona.DISTRIBUTOR || !allUsersData) {
      return {
        allDirectorsCompleted: false,
        allDirectorsApproved: false,
        completedDirectors: [],
        approvedDirectors: [],
        pendingDirectors: directorEmails,
      };
    }

    // Create Set for O(1) email lookups instead of O(n) array.includes
    const directorEmailSet = new Set(directorEmails);

    // Filter to only directors with Regtank enabled
    const directors = allUsersData.filter(
      (user) => directorEmailSet.has(user.email) && user.regtankOnboarding
    );

    // Process each director's status
    const approvedDirectors: string[] = [];

    directors.forEach((director) => {
      const { approved } = getDirectorStatus(director);

      if (approved) {
        approvedDirectors.push(director.email);
      }
    });

    // Use Set for efficient pending calculation (O(1) lookups)
    const approvedSet = new Set(approvedDirectors);
    const pendingDirectors = directorEmails.filter(
      (email) => !approvedSet.has(email)
    );

    return {
      allDirectorsApproved:
        approvedDirectors.length === directorEmails.length &&
        directorEmails.length > 0,
      approvedDirectors,
      pendingDirectors,
    };
  }, [directorEmails, allUsersData, role]);
};
